import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

interface ProductCardProps {
  id: number | string;
  category: string;
  name: string;
  originalPrice: number;
  discount: number;
  finalPrice: number;
  perPill?: number;
  quantity?: number;
  manufacturer: string;
  rating: number;
  reviewCount: number;
  image?: string;
}

const PillIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="8" width="20" height="32" rx="10" stroke="currentColor" strokeWidth="1.5" />
    <line x1="14" y1="24" x2="34" y2="24" stroke="currentColor" strokeWidth="1.5" />
    <rect x="14" y="8" width="20" height="16" rx="10" fill="currentColor" opacity="0.1" />
  </svg>
);

const BottleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="18" y="6" width="12" height="6" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M16 16C16 13.7909 17.7909 12 20 12H28C30.2091 12 32 13.7909 32 16V38C32 40.2091 30.2091 42 28 42H20C17.7909 42 16 40.2091 16 38V16Z" stroke="currentColor" strokeWidth="1.5" />
    <rect x="16" y="26" width="16" height="16" rx="2" fill="currentColor" opacity="0.1" />
    <path d="M22 20H26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Detect appropriate unit label from product name (pen, inhaler, spray, etc.)
function detectUnit(name: string): { singular: string; plural: string } {
  const n = (name || '').toLowerCase();
  if (/\bpen\b/.test(n)) return { singular: 'pen', plural: 'pens' };
  if (/inhaler|turbuhaler|rotacap/.test(n)) return { singular: 'inhaler', plural: 'inhalers' };
  if (/injection|vial|ampoule/.test(n)) return { singular: 'vial', plural: 'vials' };
  if (/spray/.test(n)) return { singular: 'spray', plural: 'sprays' };
  if (/cream|ointment|lotion|tretinoin|retin-a|tretiheal|skyntret/.test(n)) return { singular: 'tube', plural: 'tubes' };
  if (/oral jelly|sachet/.test(n)) return { singular: 'sachet', plural: 'sachets' };
  if (/drops|solution|syrup|suspension/.test(n)) return { singular: 'bottle', plural: 'bottles' };
  if (/gel\b/.test(n)) return { singular: 'tube', plural: 'tubes' };
  if (/capsule/.test(n)) return { singular: 'capsule', plural: 'capsules' };
  return { singular: 'pill', plural: 'pills' };
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  category,
  name,
  originalPrice,
  discount,
  finalPrice,
  perPill,
  quantity,
  rating,
  reviewCount,
  image
}) => {
  const isTopical = useMemo(() => ['Minoxidil'].includes(category), [category]);
  const initials = useMemo(() => {
    const words = (name || '').split(' ');
    return words.length >= 2
      ? (words[0][0] + words[1][0]).toUpperCase()
      : (name || 'P').substring(0, 2).toUpperCase();
  }, [name]);

  return (
    <Link to={`/product/${id}`} className="block group">
      <div className="relative">
        {/* Image */}
        <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-white">
          {image ? (
            <img
              src={image}
              alt={name}
              className="w-full h-full object-contain transition-transform duration-700 ease-botanical group-hover:scale-[1.04]"
            />
          ) : (
            <div className="w-full h-full bg-cream-100 flex flex-col items-center justify-center relative">
              <div className="relative mb-2">
                {isTopical ? (
                  <BottleIcon className="w-14 h-14 text-forest-400 opacity-60" />
                ) : (
                  <PillIcon className="w-14 h-14 text-forest-400 opacity-60" />
                )}
              </div>
              <span className="relative text-caption font-semibold text-forest-400 opacity-50 tracking-widest">
                {initials}
              </span>
              {name && /\d+mg/i.test(name) && (
                <span className="relative text-nano font-medium text-forest-400 opacity-40 mt-0.5">
                  {name.match(/\d+mg/i)?.[0]}
                </span>
              )}
            </div>
          )}

          {/* Discount badge */}
          {discount > 0 && (
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center px-2.5 py-1 bg-gold-400 text-forest-900 text-nano font-mono font-semibold rounded-md tracking-wide">
                -{discount}%
              </span>
            </div>
          )}

          {/* Quick view overlay */}
          <div className="absolute inset-0 bg-forest-800/0 group-hover:bg-forest-800/[0.03] transition-colors duration-500" />
        </div>

        {/* Info */}
        <div className="space-y-1.5">
          <span className="text-nano font-semibold text-forest-600 uppercase tracking-[0.12em]">
            {category}
          </span>

          <h3 className="text-body-sm font-medium text-forest-800 line-clamp-2 group-hover:text-forest-900 transition-colors duration-300 leading-snug">
            {name}
          </h3>

          <div className="flex items-baseline gap-2 pt-0.5">
            {perPill && perPill > 0 ? (
              <span className="text-[17px] font-semibold text-forest-900 tracking-tight font-mono">
                ${perPill.toFixed(2)}
              </span>
            ) : (
              <span className="text-[17px] font-semibold text-forest-900 tracking-tight font-mono">
                ${(finalPrice && typeof finalPrice === 'number') ? finalPrice.toFixed(2) : '0.00'}
              </span>
            )}
            {perPill && perPill > 0 ? (
              <span className="text-nano text-warm-400">/ {detectUnit(name).singular}</span>
            ) : null}
            {originalPrice > 0 && discount > 0 && (
              <span className="text-caption text-warm-400 line-through">
                ${originalPrice.toLocaleString()}
              </span>
            )}
          </div>
          {perPill && perPill > 0 && quantity && quantity > 0 && (
            <div className="text-nano text-warm-400 -mt-0.5">
              {quantity} {quantity === 1 ? detectUnit(name).singular : detectUnit(name).plural} &middot; ${(finalPrice && typeof finalPrice === 'number') ? finalPrice.toFixed(2) : '0.00'}
            </div>
          )}

          {rating > 0 && (
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className={`w-3 h-3 ${i < Math.round(rating) ? 'text-gold-400' : 'text-warm-200'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              {reviewCount > 0 && (
                <span className="text-nano text-warm-400">
                  ({reviewCount})
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
